/** Pure HUD logic (M2-57): mood bands, wallet floater, light-off banner, one-toast queue. */
import { effect, signal } from '@preact/signals';
import moods from '../../../../../content/lang/moods.json';
import areaPosts from '../../../../../content/lang/area-posts.json';

export const NEED_ORDER = ['hunger', 'energy', 'hygiene'];
export const BAND_LOW_BELOW = 35;
export const BAND_HIGH_FROM = 75;
export const FLOATER_MS = 1200;
export const TOAST_MS = 2500;

export function bandFor(value) {
    if (value < BAND_LOW_BELOW) return 'low';
    if (value >= BAND_HIGH_FROM) return 'high';
    return 'ok';
}

export function moodLabel(need, value) {
    const band = bandFor(value);
    return moods.find((m) => m.need === need && m.band === band)?.label ?? need;
}

/** Signed floater text for a balance change; null when unchanged. */
export function floaterFor(prev, next) {
    const d = next - prev;
    if (d === 0) return null;
    const amt = `₦${Math.abs(d).toLocaleString('en-NG')}`;
    return { sign: d > 0 ? 'up' : 'down', text: d > 0 ? `+${amt}` : `−${amt}` };
}

/** Floater signal: array of {id, sign, text}, each expiring after FLOATER_MS. Returns {floaters, stop}. */
export function createFloaters(bridge, { setTimer = setTimeout } = {}) {
    const floaters = signal([]);
    let prev = bridge.wallet.peek().naira;
    let seq = 0;
    const stop = effect(() => {
        const next = bridge.wallet.value.naira;
        const f = floaterFor(prev, next);
        prev = next;
        if (!f) return;
        const id = ++seq;
        floaters.value = [...floaters.peek(), { id, ...f }];
        setTimer(() => {
            floaters.value = floaters.peek().filter((x) => x.id !== id);
        }, FLOATER_MS);
    });
    return { floaters, stop };
}

/** Light-off post for a power state, or null while power is on. `pick` chooses the post index. */
export function lightBanner(power, pick = 0) {
    if (power !== 'off') return null;
    const posts = areaPosts.light_off;
    return posts[pick % posts.length];
}

/** Banner signal: shows a light_off post when bridge.power goes 'off', clears on any other state. */
export function createBanner(bridge, { random = Math.random } = {}) {
    const banner = signal(null);
    const stop = effect(() => {
        const p = bridge.power.value;
        banner.value = lightBanner(p, Math.floor(random() * areaPosts.light_off.length));
    });
    return { banner, stop };
}

/** One-toast-at-a-time FIFO queue. `current` is the visible toast text or null. */
export function createToastQueue({ setTimer = setTimeout, clearTimer = clearTimeout, ttl = TOAST_MS } = {}) {
    const current = signal(null);
    const pending = [];
    let timer = null;
    const show = (text) => {
        current.value = text;
        timer = setTimer(next, ttl);
    };
    function next() {
        timer = null;
        if (pending.length) show(pending.shift());
        else current.value = null;
    }
    return {
        current,
        push(text) {
            const t = String(text ?? '').trim();
            if (!t) return;
            if (current.peek() === null && timer === null) show(t);
            else pending.push(t);
        },
        dismiss() {
            if (timer !== null) clearTimer(timer);
            next();
        },
    };
}

/** Add `bridge.toast(text)` in the UI layer when the bridge lacks it. Returns the queue. */
export function ensureToast(bridge, opts) {
    const q = createToastQueue(opts);
    if (typeof bridge.toast !== 'function') bridge.toast = (text) => q.push(text);
    else {
        const orig = bridge.toast;
        bridge.toast = (text) => {
            q.push(text);
            orig(text);
        };
    }
    return q;
}
